import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

/**
 * Formate un montant en monnaie CFA (ex: 450 000)
 */
export const formatCurrency = (val) => {
    if (val === null || val === undefined || isNaN(val)) return '0';
    return Number(val).toLocaleString('fr-FR').replace(/\s/g, ' ');
};

/**
 * Parse n'importe quel format de date
 */
export const parseAnyDate = (dateVal) => {
    if (!dateVal) return null;
    if (dayjs.isDayjs(dateVal)) return dateVal;
    if (dateVal instanceof Date) return dayjs(dateVal);
    
    const str = String(dateVal).trim();
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
        return dayjs(str, 'DD/MM/YYYY');
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
        return dayjs(str);
    }
    const parsed = dayjs(dateVal);
    return parsed.isValid() ? parsed : null;
};

export const formatDate = (dateVal, format = 'DD/MM/YYYY') => {
    const d = parseAnyDate(dateVal);
    return d && d.isValid() ? d.format(format) : '-';
};

/**
 * 1. EXTRAIT DE COMPTE (Section 5.3)
 * Liste chronologique de tous les mouvements (Factures au Débit, Encaissements au Crédit)
 * avec Solde cumulé recalculé après chaque écriture.
 */
export const generateExtraitCompte = (client, factures = [], encaissements = [], startDate = null, endDate = null) => {
    const start = startDate ? parseAnyDate(startDate).startOf('day') : null;
    const end = endDate ? parseAnyDate(endDate).endOf('day') : null;

    const clientId = client?.id || client?.code || client;
    const clientName = client?.libelle || client?.nomComplet || client?.nom || (typeof client === 'string' ? client : 'Client');

    const movements = [];

    // A. Factures (DÉBIT)
    factures.forEach(fact => {
        const factClient = fact.toClient?.id || fact.toClient?.code || fact.toClient || fact.nomClient || fact.client?.id || fact.client;
        const matchesClient = !clientId || String(factClient) === String(clientId) || (typeof factClient === 'string' && factClient.toLowerCase() === clientName.toLowerCase());

        if (matchesClient) {
            const dateObj = parseAnyDate(fact.date || fact.createdAt);
            if (dateObj && dateObj.isValid()) {
                const inPeriod = (!start || dateObj.isSameOrAfter(start)) && (!end || dateObj.isSameOrBefore(end));
                if (inPeriod) {
                    const montant = Number(fact.montant || fact.total || fact.totalFacture || 0);
                    const libelle = fact.commentaire || fact.libelle || `Facture ${fact.typeFacture || ''} - ${fact.dossier?.reference || fact.numeroDossier || 'Prestation'}`;
                    movements.push({
                        id: `fact-${fact.id || fact.numeroFacture}`,
                        date: dateObj,
                        dateStr: dateObj.format('DD/MM/YYYY'),
                        piece: fact.numeroFacture || `F-${fact.id}`,
                        libelle: libelle,
                        debit: montant,
                        credit: 0,
                        type: 'facture',
                        raw: fact
                    });
                }
            }
        }
    });

    // B. Encaissements / Règlements (CRÉDIT)
    encaissements.forEach(enc => {
        const encClient = enc.client?.id || enc.client?.code || enc.client || enc.clientId || enc.nomClient;
        const matchesClient = !clientId || String(encClient) === String(clientId) || (typeof encClient === 'string' && encClient.toLowerCase() === clientName.toLowerCase());

        if (matchesClient) {
            const dateObj = parseAnyDate(enc.date || enc.dateEncaissement || enc.createdAt);
            if (dateObj && dateObj.isValid()) {
                const inPeriod = (!start || dateObj.isSameOrAfter(start)) && (!end || dateObj.isSameOrBefore(end));
                if (inPeriod) {
                    const montant = Number(enc.montant || enc.montantEncaisse || 0);
                    const mode = enc.modePaiement || enc.methode || 'Règlement';
                    const ref = enc.reference ? ` (${enc.reference})` : '';
                    movements.push({
                        id: `enc-${enc.id || enc.numeroRecu}`,
                        date: dateObj,
                        dateStr: dateObj.format('DD/MM/YYYY'),
                        piece: enc.numeroRecu || `E-${enc.id || 'REG'}`,
                        libelle: `Encaissement — ${mode}${ref}`,
                        debit: 0,
                        credit: montant,
                        type: 'encaissement',
                        raw: enc
                    });
                }
            }
        }
    });

    // Tri chronologique croissant
    movements.sort((a, b) => a.date.valueOf() - b.date.valueOf());

    // Calcul du solde cumulé
    let cumulative = 0;
    let totalDebit = 0;
    let totalCredit = 0;

    const rows = movements.map(m => {
        cumulative += (m.debit - m.credit);
        totalDebit += m.debit;
        totalCredit += m.credit;
        return {
            ...m,
            soldeCumule: cumulative
        };
    });

    return {
        clientName,
        startDateStr: start ? start.format('DD/MM/YYYY') : 'Origine',
        endDateStr: end ? end.format('DD/MM/YYYY') : 'Ce jour',
        rows,
        totalDebit,
        totalCredit,
        soldeFinal: cumulative
    };
};

/**
 * 2. RELEVÉ DE COMPTE (Section 5.4)
 * Situation des factures ouvertes (dues ou en avoir) à une date de situation
 */
export const generateReleveCompte = (client, factures = [], encaissements = [], dateSituation = null, statusFilter = 'ALL') => {
    const sitDate = dateSituation ? parseAnyDate(dateSituation).endOf('day') : dayjs().endOf('day');
    const clientId = client?.id || client?.code || client;
    const clientName = client?.libelle || client?.nomComplet || client?.nom || (typeof client === 'string' ? client : 'Client');
    const clientDelai = Number(client?.delaiPaiement || 30); // 30 jours par défaut

    const rows = [];
    let totalFacture = 0;
    let totalRegle = 0;
    let totalSoldeDu = 0;

    factures.forEach(fact => {
        const factClient = fact.toClient?.id || fact.toClient?.code || fact.toClient || fact.nomClient || fact.client?.id || fact.client;
        const matchesClient = !clientId || String(factClient) === String(clientId) || (typeof factClient === 'string' && factClient.toLowerCase() === clientName.toLowerCase());

        if (matchesClient) {
            const factDate = parseAnyDate(fact.date || fact.createdAt);
            if (factDate && factDate.isSameOrBefore(sitDate)) {
                const montant = Number(fact.montant || fact.total || fact.totalFacture || 0);
                
                // Calcul du montant déjà réglé à la date de situation
                let montantRegle = 0;
                if (Array.isArray(fact.paiements)) {
                    montantRegle = fact.paiements.reduce((sum, p) => {
                        const pDate = parseAnyDate(p.date || p.createdAt);
                        if (!pDate || pDate.isSameOrBefore(sitDate)) {
                            return sum + Number(p.montant || 0);
                        }
                        return sum;
                    }, 0);
                } else if (fact.dejaPaye !== undefined && fact.dejaPaye !== null) {
                    montantRegle = Number(fact.dejaPaye);
                }

                const soldeDu = montant - montantRegle;
                
                // Calcul de la date d'échéance (date facture + délai client)
                const echeanceDate = fact.echeance ? parseAnyDate(fact.echeance) : factDate.add(clientDelai, 'day');
                const isLate = sitDate.isAfter(echeanceDate, 'day');
                const daysLate = isLate ? sitDate.diff(echeanceDate, 'day') : 0;

                // Filtrage selon le statut demandé
                let keep = false;
                if (statusFilter === 'ALL') {
                    keep = Math.abs(soldeDu) > 0.01; // Factures ouvertes / non soldées
                } else if (statusFilter === 'TOUTES') {
                    keep = true;
                } else if (statusFilter === 'PAYEE') {
                    keep = soldeDu <= 0.01;
                } else if (statusFilter === 'IMPAYEE') {
                    keep = soldeDu > 0.01;
                }

                if (keep) {
                    rows.push({
                        id: fact.id || fact.numeroFacture,
                        numeroFacture: fact.numeroFacture || `F-${fact.id}`,
                        dateFacture: factDate.format('DD/MM/YYYY'),
                        dateFactureRaw: factDate,
                        echeance: echeanceDate.format('DD/MM/YYYY'),
                        echeanceRaw: echeanceDate,
                        isLate,
                        daysLate,
                        montantFacture: montant,
                        montantRegle: montantRegle,
                        soldeDu: soldeDu,
                        raw: fact
                    });

                    totalFacture += montant;
                    totalRegle += montantRegle;
                    totalSoldeDu += soldeDu;
                }
            }
        }
    });

    // Tri par date de facture croissante
    rows.sort((a, b) => a.dateFactureRaw.valueOf() - b.dateFactureRaw.valueOf());

    return {
        clientName,
        dateSituationStr: sitDate.format('DD/MM/YYYY'),
        rows,
        totalFacture,
        totalRegle,
        totalSoldeDu
    };
};

/**
 * 3. BALANCE GÉNÉRALE CLIENTS (Section 5.5)
 * Situation globale de tous les clients à une date donnée.
 */
export const generateBalanceGenerale = (clientsList = [], factures = [], encaissements = [], dateSituation = null) => {
    const sitDate = dateSituation ? parseAnyDate(dateSituation).endOf('day') : dayjs().endOf('day');
    
    // Regroupement par client
    const clientMap = {};

    // 1. Initialiser la liste des clients connus
    clientsList.forEach(c => {
        const id = c.id || c.code;
        const name = c.libelle || c.nomComplet || c.nom || 'Client';
        clientMap[id] = {
            id,
            nom: name,
            debit: 0,
            credit: 0,
            soldeDu: 0
        };
    });

    // 2. Agréger les factures jusqu'à la date de situation
    factures.forEach(fact => {
        const factDate = parseAnyDate(fact.date || fact.createdAt);
        if (factDate && factDate.isSameOrBefore(sitDate)) {
            const c = fact.toClient || fact.client;
            const cId = c?.id || c?.code || (typeof c === 'string' ? c : fact.nomClient) || 'CLIENT_DIVERS';
            const cName = c?.libelle || c?.nomComplet || c?.nom || fact.nomClient || (typeof c === 'string' ? c : 'Client Divers');

            if (!clientMap[cId]) {
                clientMap[cId] = { id: cId, nom: cName, debit: 0, credit: 0, soldeDu: 0 };
            }

            const montant = Number(fact.montant || fact.total || fact.totalFacture || 0);
            clientMap[cId].debit += montant;

            // Si des paiements sont intégrés à la facture
            if (Array.isArray(fact.paiements)) {
                fact.paiements.forEach(p => {
                    const pDate = parseAnyDate(p.date || p.createdAt);
                    if (!pDate || pDate.isSameOrBefore(sitDate)) {
                        clientMap[cId].credit += Number(p.montant || 0);
                    }
                });
            } else if (fact.dejaPaye) {
                clientMap[cId].credit += Number(fact.dejaPaye);
            }
        }
    });

    // 3. Agréger les encaissements directs si non inclus dans les factures
    encaissements.forEach(enc => {
        const encDate = parseAnyDate(enc.date || enc.dateEncaissement || enc.createdAt);
        if (encDate && encDate.isSameOrBefore(sitDate)) {
            const c = enc.client;
            const cId = c?.id || c?.code || enc.clientId || (typeof c === 'string' ? c : enc.nomClient) || 'CLIENT_DIVERS';
            const cName = c?.libelle || c?.nomComplet || enc.nomClient || 'Client Divers';

            if (!clientMap[cId]) {
                clientMap[cId] = { id: cId, nom: cName, debit: 0, credit: 0, soldeDu: 0 };
            }
            // Si pas déjà sommé via fact.paiements
            if (!enc.isFactureLinked) {
                clientMap[cId].credit += Number(enc.montant || enc.montantEncaisse || 0);
            }
        }
    });

    let totalGeneralDebit = 0;
    let totalGeneralCredit = 0;
    let totalGeneralSolde = 0;

    const rows = Object.values(clientMap)
        .map(c => {
            const solde = c.debit - c.credit;
            return {
                ...c,
                soldeDu: solde
            };
        })
        .filter(c => Math.abs(c.debit) > 0 || Math.abs(c.credit) > 0 || Math.abs(c.soldeDu) > 0)
        .sort((a, b) => b.soldeDu - a.soldeDu);

    rows.forEach(r => {
        totalGeneralDebit += r.debit;
        totalGeneralCredit += r.credit;
        totalGeneralSolde += r.soldeDu;
    });

    return {
        dateSituationStr: sitDate.format('DD/MM/YYYY'),
        rows,
        totalGeneralDebit,
        totalGeneralCredit,
        totalGeneralSolde
    };
};

/**
 * 4. BALANCE ÂGÉE CLIENTS (Section 5.5 & Pages 19-20)
 * Répartit le solde dû de chaque client par tranche d'ancienneté du retard :
 * - Non échu : Retard <= 0
 * - De 0 à 30 jours : 1 <= Retard <= 30
 * - De 31 à 60 jours : 31 <= Retard <= 60
 * - De 61 à 90 jours : 61 <= Retard <= 90
 * - Plus de 90 jours : Retard > 90
 */
export const generateBalanceAgee = (clientsList = [], factures = [], encaissements = [], dateSituation = null) => {
    const sitDate = dateSituation ? parseAnyDate(dateSituation).endOf('day') : dayjs().endOf('day');

    const clientMap = {};

    factures.forEach(fact => {
        const factDate = parseAnyDate(fact.date || fact.createdAt);
        if (factDate && factDate.isSameOrBefore(sitDate)) {
            const c = fact.toClient || fact.client;
            const cId = c?.id || c?.code || (typeof c === 'string' ? c : fact.nomClient) || 'CLIENT_DIVERS';
            const cName = c?.libelle || c?.nomComplet || c?.nom || fact.nomClient || (typeof c === 'string' ? c : 'Client Divers');
            const clientDelai = Number(c?.delaiPaiement || 30);

            if (!clientMap[cId]) {
                clientMap[cId] = {
                    id: cId,
                    nom: cName,
                    soldeDu: 0,
                    nonEchu: 0,
                    tranche0_30: 0,
                    tranche31_60: 0,
                    tranche61_90: 0,
                    tranchePlus90: 0
                };
            }

            const montant = Number(fact.montant || fact.total || fact.totalFacture || 0);
            
            // Paiements partiels
            let paye = 0;
            if (Array.isArray(fact.paiements)) {
                paye = fact.paiements.reduce((sum, p) => {
                    const pDate = parseAnyDate(p.date || p.createdAt);
                    if (!pDate || pDate.isSameOrBefore(sitDate)) return sum + Number(p.montant || 0);
                    return sum;
                }, 0);
            } else if (fact.dejaPaye) {
                paye = Number(fact.dejaPaye);
            }

            const solde = montant - paye;

            if (Math.abs(solde) > 0.01) {
                clientMap[cId].soldeDu += solde;

                // Calcul du retard
                const echeance = fact.echeance ? parseAnyDate(fact.echeance) : factDate.add(clientDelai, 'day');
                const retardJours = sitDate.diff(echeance, 'day');

                if (retardJours <= 0) {
                    clientMap[cId].nonEchu += solde;
                } else if (retardJours >= 1 && retardJours <= 30) {
                    clientMap[cId].tranche0_30 += solde;
                } else if (retardJours >= 31 && retardJours <= 60) {
                    clientMap[cId].tranche31_60 += solde;
                } else if (retardJours >= 61 && retardJours <= 90) {
                    clientMap[cId].tranche61_90 += solde;
                } else {
                    clientMap[cId].tranchePlus90 += solde;
                }
            }
        }
    });

    const rows = Object.values(clientMap)
        .filter(c => Math.abs(c.soldeDu) > 0.01)
        .sort((a, b) => b.soldeDu - a.soldeDu);

    const totals = {
        soldeDu: 0,
        nonEchu: 0,
        tranche0_30: 0,
        tranche31_60: 0,
        tranche61_90: 0,
        tranchePlus90: 0
    };

    rows.forEach(r => {
        totals.soldeDu += r.soldeDu;
        totals.nonEchu += r.nonEchu;
        totals.tranche0_30 += r.tranche0_30;
        totals.tranche31_60 += r.tranche31_60;
        totals.tranche61_90 += r.tranche61_90;
        totals.tranchePlus90 += r.tranchePlus90;
    });

    return {
        dateSituationStr: sitDate.format('DD/MM/YYYY'),
        rows,
        totals
    };
};
